import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useAuth, useUser, useClerk, SignIn } from '@clerk/clerk-react';
import { dark } from '@clerk/themes';

/** Rollen die Zugriff auf den Scanner haben. */
const ALLOWED_ROLES = ['member', 'team', 'admin'] as const;

/**
 * Anzeige bei fehlender Berechtigung mit Sign-Out-Möglichkeit.
 * Ausgelagert damit `useClerk` regelkonform in einer Komponente verwendet wird.
 */
function AccessDenied() {
  const { signOut } = useClerk();

  return (
    <div
      style={{
        minHeight: '100dvh',
        backgroundColor: '#0f0f23',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
        gap: '24px',
        fontFamily: 'system-ui, sans-serif',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          width: '72px',
          height: '72px',
          borderRadius: '50%',
          backgroundColor: '#2d1515',
          border: '2px solid #991b1b',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '28px',
          color: '#f87171',
          fontWeight: 'bold',
        }}
      >
        X
      </div>
      <h1 style={{ fontSize: '20px', fontWeight: 'bold', color: '#f87171', margin: 0 }}>
        Kein Zugriff
      </h1>
      <p style={{ color: '#aaa', margin: 0, maxWidth: '320px' }}>
        Du hast keine Berechtigung, den Scanner zu verwenden.
      </p>
      <button
        onClick={() => void signOut()}
        style={{
          minHeight: '48px',
          padding: '12px 32px',
          fontSize: '16px',
          fontWeight: 'bold',
          backgroundColor: 'transparent',
          color: '#f87171',
          border: '2px solid #f87171',
          borderRadius: '8px',
          cursor: 'pointer',
        }}
      >
        Abmelden
      </button>
    </div>
  );
}

/**
 * Auth-Guard Layout-Route.
 * Zeigt die Clerk Sign-In Komponente wenn der Nutzer nicht eingeloggt ist.
 * Prüft nach dem Login ob der Nutzer eine erlaubte Rolle hat.
 */
function AuthenticatedLayout() {
  const { isLoaded, isSignedIn } = useAuth();
  const { user, isLoaded: isUserLoaded } = useUser();

  if (!isLoaded) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          backgroundColor: '#0f0f23',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#aaa',
          fontSize: '16px',
        }}
      >
        Lade...
      </div>
    );
  }

  if (!isSignedIn) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          backgroundColor: '#0f0f23',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px',
          gap: '24px',
        }}
      >
        <div style={{ textAlign: 'center' }}>
          <p style={{ fontSize: '13px', color: '#aaa', margin: 0 }}>
            Faninitiative Spandau — Scanner
          </p>
        </div>
        <SignIn appearance={{ baseTheme: dark }} />
      </div>
    );
  }

  if (!isUserLoaded || !user) {
    return (
      <div
        style={{
          minHeight: '100dvh',
          backgroundColor: '#0f0f23',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#aaa',
          fontSize: '16px',
        }}
      >
        Lade...
      </div>
    );
  }

  const role = user.publicMetadata?.role as string | undefined;
  const hasPermission = ALLOWED_ROLES.includes(role as (typeof ALLOWED_ROLES)[number]);

  if (!hasPermission) {
    return <AccessDenied />;
  }

  return <Outlet />;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Route = createFileRoute('/_authenticated' as any)({
  beforeLoad: ({ context }) => {
    // context.queryClient is available here for prefetching if needed
    void context;
  },
  component: AuthenticatedLayout,
});
